import { localizeFindingText } from './findingLocale'

export type FindingReview = {
  title: string
  what: string
  why: string
  where: string
  snippet: string
  fix: string
}

type FindingLike = {
  title?: string | null
  description?: string | null
  impact?: string | null
  primary_location?: string | null
  line_number?: number | null
  remediation_guidance?: string | null
  extra_metadata?: Record<string, unknown> | null
  metadata?: Record<string, unknown> | null
  evidence?: Array<{ content_preview?: string | null }> | null
}

function asText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

function normalizeSnippet(snippet: string): string {
  const t = (snippet || '').trim()
  if (!t) return ''
  // Knowledge rules may match only the caret marker (`^`), which is not real code.
  if (/^\^+$/.test(t)) return ''
  return t
}

function pickBestSnippet(...candidates: Array<string | null | undefined>): string {
  const normalized = candidates
    .map((value) => normalizeSnippet(asText(value)))
    .filter(Boolean)
  if (!normalized.length) return ''
  // Prefer multiline / longer windows for engineer UX.
  return normalized.sort((a, b) => {
    const aScore = (a.includes('\n') ? 1000 : 0) + a.length
    const bScore = (b.includes('\n') ? 1000 : 0) + b.length
    return bScore - aScore
  })[0]
}

function stripMashed(raw: string): { what: string; why: string; where: string; snippet: string } {
  let rest = (raw || '').replace(/\r\n/g, '\n').trim()
  if (!rest) return { what: '', why: '', where: '', snippet: '' }

  let snippet = ''
  let where = ''
  let why = ''

  const snippetParts = rest.split(/\n+\s*Snippet:\s*/i)
  if (snippetParts.length > 1) {
    snippet = snippetParts.slice(1).join('\n').trim()
    rest = snippetParts[0]
  }

  const evidenceParts = rest.split(/\n+\s*Evidence:\s*/i)
  if (evidenceParts.length > 1) {
    where = evidenceParts.slice(1).join('\n').trim()
    rest = evidenceParts[0]
  }

  const locationParts = rest.split(/\n+\s*Location:\s*/i)
  if (locationParts.length > 1) {
    if (!where) where = locationParts.slice(1).join('\n').trim()
    rest = locationParts[0]
  }

  const whyParts = rest.split(/\n+\s*Why it matters:\s*/i)
  if (whyParts.length > 1) {
    why = whyParts.slice(1).join('\n').trim()
    rest = whyParts[0]
  }

  return {
    what: rest.replace(/\n{3,}/g, '\n\n').trim(),
    why,
    where,
    snippet,
  }
}

export function buildFindingReview(finding: FindingLike, locale: string = 'en'): FindingReview {
  const extra = finding.extra_metadata || finding.metadata || {}
  const mashed = stripMashed(finding.description || '')
  const whereFromFields = [finding.primary_location, finding.line_number]
    .filter((part) => part !== null && part !== undefined && part !== '')
    .join(':')

  const what = mashed.what || asText(extra.message) || (finding.description || '').trim()
  const why = mashed.why || (finding.impact || '').trim() || asText(extra.why_it_matters)
  const where = mashed.where || whereFromFields || asText(extra.file_path)
  const evidenceSnippet = (finding.evidence || [])
    .map((item) => asText(item?.content_preview))
    .find(Boolean)
  const snippet = pickBestSnippet(mashed.snippet, asText(extra.snippet), evidenceSnippet)
  const fix = (finding.remediation_guidance || '').trim() || asText(extra.remediation)

  return {
    title: localizeFindingText(locale, (finding.title || '').trim()),
    what: localizeFindingText(locale, what),
    why: localizeFindingText(locale, why),
    where,
    snippet,
    fix: localizeFindingText(locale, fix),
  }
}

export function paragraphize(text: string): string[] {
  return (text || '')
    .split(/\n{2,}/)
    .map((part) => part.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
}
